// src/admin/CreateForm.jsx
import React, { useState, useEffect, useContext, } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Paper, Box, TextField, Button, Typography, Grid, FormControlLabel, Checkbox, ListItemText, Autocomplete } from '@mui/material';
import { QUESTION_TYPES } from '../constants/questionTypes';
import { FORM_TYPES } from '../constants/formTypes';
import { useNavigate, useParams } from 'react-router-dom';
import * as endPoints from '../assets/js/endpoints';
import { useNotification } from '../assets/contexts/notificationContext';
import { UserContext } from '../assets/contexts/userContext';
import { useFadeInOnScroll } from '../theme/global';
import Loader from '../components/loader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CreateForm = () => {
  const { id } = useParams(); // Si existe, estamos en modo edición
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const { showNotification } = useNotification();
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const containerRef = useFadeInOnScroll([loading]);



  const [formData, setFormData] = useState({
    nombre: '',
    form_type_id: 1,
    descripcion: '',
    email_destino: '',
    cierre: '',
    preguntas: [] // Array de preguntas
  });

  // Si se está editando, se carga el formulario existente
  useEffect(() => {
    if (id) {
      setLoading(true);
      endPoints.getFormById(id)
        .then((data) => {
          setFormData({
            nombre: data.nombre,
            form_type_id: data.form_type_id,
            descripcion: data.descripcion,
            email_destino: data.email_destino,
            cierre: data.cierre,
            // Se asume que data.questions tiene la estructura con 'texto', 'tipo', etc.
            preguntas: data.questions.map(q => ({
              texto: q.texto,
              tipo: q.tipo,
              opciones: q.opciones || '',
              obligatorio: q.obligatorio
            }))
          });
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedPreguntas = [...formData.preguntas];
    updatedPreguntas[index] = { ...updatedPreguntas[index], [field]: value };
    setFormData({ ...formData, preguntas: updatedPreguntas });
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      preguntas: [
        ...formData.preguntas,
        { texto: '', tipo: QUESTION_TYPES[0].value, opciones: '', obligatorio: false }
      ]
    });
  };

  const removeQuestion = (index) => {
    const updatedPreguntas = formData.preguntas.filter((_, i) => i !== index);
    setFormData({ ...formData, preguntas: updatedPreguntas });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepara el payload en formato JSON
    const dataToSend = {
      nombre: formData.nombre,
      form_type_id: formData.form_type_id, // ojo: form_type_id
      descripcion: formData.descripcion,
      email_destino: formData.email_destino,
      cierre: formData.cierre,
      preguntas: formData.preguntas,
    };

    setLoading(true);
    try {
      if (id) {
        // en edición también le pasas directamente el objeto
        await endPoints.updateForm(id, dataToSend);
        showNotification('Formulario actualizado correctamente', 'success');
      } else {
        await endPoints.createForm(dataToSend);
        showNotification('Formulario creado correctamente', 'success');
      }

      user.hasRole('admin')
        ? navigate('/admin/manageForms')
        : navigate('/employee/forms');
    } catch (err) {
      const serverMsg = err.response?.data?.error
        || (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors[0]
            : null);
      const msg = serverMsg || 'Error guardando el formulario';
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <>
    <Helmet>
      <title>Crear Formulario | CES</title>
      <meta name="description" content="Diseña un nuevo formulario." />
    </Helmet>
    <Container ref={containerRef} maxWidth="md" className="fade-up" sx={{ mt: 5, mb: 5, p: 2 }}>
      <Box sx={{ display:'flex', justifyContent:'flex-end', mb:2 }}>
                <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Volver</Button>
              </Box>
      <Paper sx={{ p: 5, mx: 'auto', maxWidth: 900 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3}}>
          {id ? 'Actualizar Formulario' : 'Crear Formulario'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth 
                label="Nombre" 
                name="nombre" 
                value={formData.nombre} 
                onChange={handleChange} 
                required 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={FORM_TYPES}
                value={FORM_TYPES.find(t => t.value === formData.form_type_id) || null}
                onChange={(_event, newValue) => {
                  setFormData(prev => ({ ...prev, form_type_id: newValue ? newValue.value : '' }));
                }}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={params => (
                  <TextField {...params} label="Tipo" required fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            {/* {formData.form_type_id === 1 && ( */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email destino"
                name="email_destino"
                value={formData.email_destino}
                onChange={handleChange}
              />
            </Grid>
            {/* )} */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cierre"
                name="cierre"
                value={formData.cierre} 
                onChange={handleChange} 
              />
            </Grid>
            {/* Sección de Preguntas */}
            <Grid item xs={12} >
              <Typography variant="h6">Preguntas</Typography>
            </Grid>
            {formData.preguntas.map((pregunta, index) => (
              <Grid item xs={12} key={index}>
                <Container maxWidth="lg"   sx={{
                    mt: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                <Grid container spacing={2}  xs={12} sm={12} sx={{ mb: 2, border: '1px solid #ccc', p: 1, borderRadius: 1, justifyContent: 'center', alignItems: 'center',  width: '100%',}}>
                <Grid item xs={12} sm={12}>
                  <TextField 
                    fullWidth 
                    label="Texto de la pregunta" 
                    value={pregunta.texto} 
                    onChange={(e) => handleQuestionChange(index, 'texto', e.target.value)} 
                    required 
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Autocomplete
                    options={QUESTION_TYPES}
                    value={QUESTION_TYPES.find(t => t.value === pregunta.tipo) || null}
                    onChange={(_event, newValue) => handleQuestionChange(index, 'tipo', newValue ? newValue.value : '')}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <ListItemText
                          primary={option.label}
                          secondary={option.description}
                          secondaryTypographyProps={{ variant: 'caption', color: 'textSecondary' }}
                        />
                      </li>
                    )}
                    renderInput={params => (
                      <TextField {...params} label="Tipo" required fullWidth />
                    )}
                  />
                </Grid>
               {['opcion_multiple', 'checkbox'].includes(pregunta.tipo) && (

                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      label="Opciones (separadas por coma)"
                      value={pregunta.opciones}

                      onChange={(e) => handleQuestionChange(index, 'opciones', e.target.value)}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={Boolean(pregunta.obligatorio)} 
                        onChange={(e) => handleQuestionChange(index, 'obligatorio', e.target.checked)} 
                      />
                    }
                    label="Obligatoria"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" color="error" onClick={() => removeQuestion(index)}>
                    Eliminar Pregunta
                  </Button>
                </Grid>
            </Grid>
              </Container>
              </Grid>
            ))}
            <Grid item xs={12} >
              <Button variant="contained" onClick={addQuestion}>
                Añadir Pregunta
              </Button>
            </Grid>
           <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box>
                <Button variant="contained" type="submit">
                  {id ? 'Actualizar' : 'Crear'}
                </Button>
                <Button variant="contained" sx={{ ml: 2 }} onClick={() => navigate('/admin/manageForms')}>
                  Cancelar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
    </>
  );
};

export default CreateForm;
